import {
  CareChatMessage,
  CareChatMessageType,
  CareChatSenderType,
  CareChatSendStatus,
  CARE_CHAT_MESSAGE_TYPE,
  CARE_CHAT_SENDER_TYPE,
  CARE_CHAT_SEND_STATUS
} from '@web/types/careChat'

type JsonObject = Record<string, unknown>

const messageTypeValues = Object.values(CARE_CHAT_MESSAGE_TYPE) as string[]

function isObject (value: unknown): value is JsonObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function parseString (json: JsonObject, key: string): string | undefined {
  const value = json[key]
  if (value == null) return undefined
  return String(value)
}

function parseInteger (json: JsonObject, key: string): number | undefined {
  const value = json[key]
  if (typeof value === 'number' && Number.isFinite(value)) return Math.trunc(value)
  if (value == null) return undefined
  const parsed = parseInt(String(value), 10)
  return Number.isNaN(parsed) ? undefined : parsed
}

function extractMessageData (json: JsonObject): JsonObject {
  const message = json.message
  return isObject(message) ? message : json
}

function parseMessageType (messageData: JsonObject, json: JsonObject): CareChatMessageType | undefined {
  const typeString = parseString(messageData, 'type') ?? parseString(json, 'type')
  if (!typeString) return undefined
  return messageTypeValues.includes(typeString) ? (typeString as CareChatMessageType) : undefined
}

function parseSenderType (json: JsonObject): CareChatSenderType | undefined {
  if (json.is_out === true) return CARE_CHAT_SENDER_TYPE.USER
  if (json.is_out === false) return CARE_CHAT_SENDER_TYPE.MANAGER

  const senderTypeRaw = json.sender_type
  if (typeof senderTypeRaw === 'string') {
    if (senderTypeRaw === CARE_CHAT_SENDER_TYPE.USER) return CARE_CHAT_SENDER_TYPE.USER
    if (senderTypeRaw === CARE_CHAT_SENDER_TYPE.MANAGER) return CARE_CHAT_SENDER_TYPE.MANAGER
  }

  const sender = json.sender
  const receiver = json.receiver
  if (isObject(sender)) {
    return isObject(receiver) ? CARE_CHAT_SENDER_TYPE.USER : CARE_CHAT_SENDER_TYPE.MANAGER
  }

  return undefined
}

function parseSenderName (json: JsonObject): string | undefined {
  if (json.sender_name != null) return String(json.sender_name)

  const author = json.author
  if (isObject(author) && author.name != null) return String(author.name)

  const sender = json.sender
  if (isObject(sender) && sender.name != null) return String(sender.name)

  return undefined
}

function parseMediaUrl (messageData: JsonObject, json: JsonObject): string | undefined {
  if (messageData.media != null) return String(messageData.media)
  if (json.media != null) return String(json.media)
  if (json.media_url != null) return String(json.media_url)
  return undefined
}

function parseThumbnailUrl (json: JsonObject): string | undefined {
  for (const key of ['photo', 'video']) {
    const nested = json[key]
    if (isObject(nested) && nested.thumbnail != null) {
      return String(nested.thumbnail)
    }
  }
  if (json.thumbnail != null) return String(json.thumbnail)
  return undefined
}

function parseTimestamp (json: JsonObject): number | undefined {
  const createdAt = json.created_at
  if (typeof createdAt === 'number') {
    return createdAt > 9999999999 ? Math.trunc(createdAt / 1000) : createdAt
  }
  const timestamp = json.timestamp
  if (typeof timestamp === 'number') return timestamp
  const msec = json.msec_timestamp
  if (typeof msec === 'number') return Math.trunc(msec / 1000)

  const fallback = parseInt(String(createdAt ?? timestamp ?? ''), 10)
  return Number.isNaN(fallback) ? undefined : fallback
}

function parseQuotedMessageId (json: JsonObject): string | undefined {
  if (json.quoted_message_id != null) return String(json.quoted_message_id)
  const replyTo = json.reply_to
  if (isObject(replyTo)) {
    const replyMessage = replyTo.message
    if (isObject(replyMessage)) {
      return parseString(replyMessage, 'id') ?? parseString(replyMessage, 'msgid')
    }
  }
  return undefined
}

function parseQuotedMessageText (json: JsonObject): string | undefined {
  if (json.quoted_message_text != null) return String(json.quoted_message_text)
  const replyTo = json.reply_to
  if (isObject(replyTo)) {
    const replyMessage = replyTo.message
    if (isObject(replyMessage)) {
      return parseString(replyMessage, 'text')
    }
  }
  return undefined
}

function parseSendStatus (messageData: JsonObject, json: JsonObject): CareChatSendStatus | undefined {
  const deliveryStatus = messageData.delivery_status ?? json.delivery_status

  if (isObject(deliveryStatus)) {
    const statusCode = deliveryStatus.status_code
    if (statusCode === -1) return CARE_CHAT_SEND_STATUS.SENDING
    if (statusCode === 1 || statusCode === 2) return CARE_CHAT_SEND_STATUS.SENT
    return undefined
  }

  if (deliveryStatus === -1) return CARE_CHAT_SEND_STATUS.SENDING
  if (deliveryStatus === 'sent' || deliveryStatus === 'read') return CARE_CHAT_SEND_STATUS.SENT
  if (deliveryStatus === 1 || deliveryStatus === 2) return CARE_CHAT_SEND_STATUS.SENT

  return undefined
}

export function mapAmoCrmMessage (json: JsonObject): CareChatMessage {
  const messageData = extractMessageData(json)

  return {
    id: parseString(messageData, 'id') ?? parseString(json, 'id'),
    conversationId: parseString(json, 'conversation_id'),
    text: parseString(messageData, 'text') ?? parseString(json, 'text'),
    type: parseMessageType(messageData, json),
    senderType: parseSenderType(json),
    senderName: parseSenderName(json),
    mediaUrl: parseMediaUrl(messageData, json),
    thumbnailUrl: parseThumbnailUrl(json),
    fileName: parseString(messageData, 'file_name') ?? parseString(json, 'file_name'),
    fileSize: parseInteger(messageData, 'file_size') ?? parseInteger(json, 'file_size'),
    timestamp: parseTimestamp(json),
    quotedMessageId: parseQuotedMessageId(json),
    quotedMessageText: parseQuotedMessageText(json),
    sendStatus: parseSendStatus(messageData, json)
  }
}

export function mapAmoCrmHistoryPayload (payload: JsonObject): CareChatMessage[] {
  const list = payload.messages
  if (!Array.isArray(list)) return []
  const result: CareChatMessage[] = []
  for (const item of list) {
    if (isObject(item)) {
      result.push(mapAmoCrmMessage(item))
    }
  }
  return result
}
